import { useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
import { SettingsProvider } from './context/SettingsContext';
import { HistoryProvider } from './context/HistoryContext';
import { CalculationHistoryEntry } from './engine/types';
import { ThemeProvider } from './context/ThemeContext';
import { ProjectProvider } from './context/ProjectContext';
import { ToastProvider } from './context/ToastContext';
import { Navigation, Header, SettingsPanel, HistoryPanel, ProjectPanel, ShortcutsPanel } from './components/layout';
import { ScrollToTop, ToastContainer, CalculatorSession, markCalculatorActivity, SESSION_CALCULATORS } from './components/common';
import { PWAUpdatePrompt } from './components/common/PWAUpdatePrompt';
import { useKeyboardShortcuts, usePersistedState } from './hooks';
import { AuthGate } from './auth/AuthGate';
import { logUsage } from './auth/usage';
import { checkIsAdmin } from './auth/api';
import { authEnabled } from './auth/msal';
import './styles/global.css';
import './styles/calculator-layout.css';
import './App.css';

// Lazy-loaded so calculator-only users pay no bundle cost for the designer.
const ProjectDesigner = lazy(() => import('./components/designer/ProjectDesigner'));

// Each calculator page is its own chunk, fetched when first opened (the
// service worker still precaches them all, so offline use is unchanged).
const WireAmpacity = lazy(() => import('./components/calculators/WireAmpacity').then((m) => ({ default: m.WireAmpacity })));
const ConduitSizing = lazy(() => import('./components/calculators/ConduitSizing').then((m) => ({ default: m.ConduitSizing })));
const VoltageDrop = lazy(() => import('./components/calculators/VoltageDrop').then((m) => ({ default: m.VoltageDrop })));
const MotorCalc = lazy(() => import('./components/calculators/MotorCalc').then((m) => ({ default: m.MotorCalc })));
const FeederSizing = lazy(() => import('./components/calculators/FeederSizing').then((m) => ({ default: m.FeederSizing })));
const MultiMotorFeeder = lazy(() => import('./components/calculators/MultiMotorFeeder').then((m) => ({ default: m.MultiMotorFeeder })));
const Transformer = lazy(() => import('./components/calculators/Transformer').then((m) => ({ default: m.Transformer })));
const Generator = lazy(() => import('./components/calculators/Generator').then((m) => ({ default: m.Generator })));
const LoadAnalysis = lazy(() => import('./components/calculators/LoadAnalysis').then((m) => ({ default: m.LoadAnalysis })));
const PLCIOList = lazy(() => import('./components/calculators/PLCIOList').then((m) => ({ default: m.PLCIOList })));
const PanelSchedule = lazy(() => import('./components/calculators/PanelSchedule').then((m) => ({ default: m.PanelSchedule })));
const HazardousLocations = lazy(() => import('./components/calculators/HazardousLocations').then((m) => ({ default: m.HazardousLocations })));
// Lazy-loaded — only admins ever open it.
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));

type CalculatorSection =
  | 'project-designer'
  | 'ampacity'
  | 'conduit'
  | 'voltage-drop'
  | 'motor'
  | 'feeder'
  | 'multi-motor-feeder'
  | 'panel-schedule'
  | 'transformer'
  | 'generator'
  | 'load-analysis'
  | 'plc-io'
  | 'hazardous-locations'
  | 'admin';

const SECTION_TITLES: Record<CalculatorSection, string> = {
  'project-designer': 'Project Designer',
  ampacity: 'Wire Ampacity Calculator',
  conduit: 'Conduit Sizing Calculator',
  'voltage-drop': 'Voltage Drop Calculator',
  motor: 'Motor Calculations',
  feeder: 'Feeder/Service Sizing Calculator',
  'multi-motor-feeder': 'Multi-Motor Feeder Calculator',
  'panel-schedule': 'Panel Schedule',
  transformer: 'Transformer Calculator',
  generator: 'Generator Calculator',
  'load-analysis': 'Load Analysis',
  'plc-io': 'S7-1200 PLC I/O List',
  'hazardous-locations': 'Hazardous Locations',
  admin: 'Admin Dashboard',
};

const NAV_COLLAPSED_KEY = 'bgd-electrical-calc-nav-collapsed';

// Sections in menu order (see NAV_GROUPS in layout/Navigation.tsx); Ctrl+1-9
// follow it, so keep the two lists and ShortcutsPanel in step.
const SECTION_ORDER: CalculatorSection[] = [
  'project-designer',
  'motor',
  'transformer',
  'generator',
  'ampacity',
  'feeder',
  'conduit',
  'voltage-drop',
  'multi-motor-feeder',
  'hazardous-locations',
  'panel-schedule',
  'load-analysis',
  'plc-io',
  'admin',
];

function AppContent() {
  // Persisted so a reload returns to the section you were in (#28) — important
  // for the Project Designer where reload + autosave recovery go together.
  const [storedSection, setStoredSection] = usePersistedState<CalculatorSection>(
    'bgd-electrical-calc-active-section',
    'ampacity'
  );
  // 'admin' is only reachable when auth is on — with auth off the app must
  // render exactly as if the section didn't exist.
  const activeSection =
    SECTION_ORDER.includes(storedSection) && (storedSection !== 'admin' || authEnabled)
      ? storedSection
      : 'ampacity';
  const setActiveSection = setStoredSection;
  // Admin nav visibility: probe once per app load. Auth off → no probe, no entry.
  const [isAdminUser, setIsAdminUser] = useState(false);
  useEffect(() => {
    if (!authEnabled) return;
    let cancelled = false;
    void checkIsAdmin().then((admin) => {
      if (!cancelled) setIsAdminUser(admin);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    const stored = localStorage.getItem(NAV_COLLAPSED_KEY);
    return stored === 'true';
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [restoreKey, setRestoreKey] = useState(0);

  const currentTitle = useMemo(() => SECTION_TITLES[activeSection], [activeSection]);

  // Handle history entry restore
  const handleHistoryRestore = useCallback((entry: CalculationHistoryEntry) => {
    // Write saved inputs back to localStorage
    if (entry.inputs) {
      Object.entries(entry.inputs).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });
    }
    // A restored entry is fresh work: don't let the idle reset clear it on mount
    markCalculatorActivity(entry.calculatorType);
    // Switch to the calculator and force remount
    setActiveSection(entry.calculatorType as CalculatorSection);
    setRestoreKey(prev => prev + 1);
    setHistoryOpen(false);
  }, [setActiveSection]);

  // Close all panels
  const closeAllPanels = useCallback(() => {
    setSettingsOpen(false);
    setHistoryOpen(false);
    setProjectOpen(false);
    setShortcutsOpen(false);
    setNavOpen(false);
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    // Escape closes panels
    {
      key: 'Escape',
      handler: closeAllPanels,
    },
    // Ctrl+, opens settings
    {
      key: ',',
      modifiers: ['ctrl'],
      handler: () => {
        closeAllPanels();
        setSettingsOpen(true);
      },
    },
    // Ctrl+H opens history
    {
      key: 'h',
      modifiers: ['ctrl'],
      handler: () => {
        closeAllPanels();
        setHistoryOpen(true);
      },
    },
    // Ctrl+P opens project panel
    {
      key: 'p',
      modifiers: ['ctrl'],
      handler: () => {
        closeAllPanels();
        setProjectOpen(true);
      },
    },
    // ? opens keyboard shortcuts
    {
      key: '?',
      handler: () => {
        closeAllPanels();
        setShortcutsOpen(true);
      },
    },
    // Ctrl+1-9 switches calculators
    ...SECTION_ORDER.map((section, idx) => ({
      key: String(idx + 1),
      modifiers: ['ctrl'] as ('ctrl')[],
      handler: () => {
        setActiveSection(section);
        closeAllPanels();
      },
    })),
  ]);

  // Persist nav collapsed state
  useEffect(() => {
    localStorage.setItem(NAV_COLLAPSED_KEY, String(navCollapsed));
  }, [navCollapsed]);

  const renderCalculator = () => {
    const raw = renderPage();
    const page = raw && (
      <Suspense fallback={<div className="calculator-loading" aria-busy="true" />}>{raw}</Suspense>
    );
    // Single-calculation pages reset after 10 idle minutes and list their recents.
    return page && SESSION_CALCULATORS[activeSection] ? (
      <CalculatorSession key={activeSection} id={activeSection} title={currentTitle}>
        {page}
      </CalculatorSession>
    ) : page;
  };

  const renderPage = () => {
    switch (activeSection) {
      case 'project-designer':
        return (
          <Suspense
            fallback={
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                Loading Project Designer…
              </div>
            }
          >
            <ProjectDesigner />
          </Suspense>
        );
      case 'ampacity':
        return <WireAmpacity />;
      case 'conduit':
        return <ConduitSizing />;
      case 'voltage-drop':
        return <VoltageDrop />;
      case 'motor':
        return <MotorCalc />;
      case 'feeder':
        return <FeederSizing />;
      case 'multi-motor-feeder':
        return <MultiMotorFeeder />;
      case 'panel-schedule':
        return <PanelSchedule />;
      case 'transformer':
        return <Transformer />;
      case 'generator':
        return <Generator />;
      case 'load-analysis':
        return <LoadAnalysis />;
      case 'plc-io':
        return <PLCIOList />;
      case 'hazardous-locations':
        return <HazardousLocations />;
      case 'admin':
        return (
          <Suspense
            fallback={
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                Loading Admin Dashboard…
              </div>
            }
          >
            <AdminDashboard />
          </Suspense>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`app ${navCollapsed ? 'nav-collapsed' : ''}`}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Navigation
        activeSection={activeSection}
        onNavigate={(section) => {
          logUsage('nav', { section });
          setActiveSection(section as CalculatorSection);
        }}
        isOpen={navOpen}
        onToggle={() => setNavOpen(!navOpen)}
        isCollapsed={navCollapsed}
        onCollapse={() => setNavCollapsed(!navCollapsed)}
        showAdmin={isAdminUser}
      />

      <Header
        onMenuToggle={() => setNavOpen(!navOpen)}
        onSettingsToggle={() => setSettingsOpen(true)}
        onHistoryToggle={() => setHistoryOpen(true)}
        onProjectToggle={() => setProjectOpen(true)}
        title={currentTitle}
        onShortcutsToggle={() => { closeAllPanels(); setShortcutsOpen(true); }}
      />

      <main
        id="main-content"
        className={`main-content ${activeSection === 'project-designer' ? 'main-content-designer' : ''}`}
        tabIndex={-1}
        key={restoreKey}
      >
        {renderCalculator()}
      </main>

      <ScrollToTop />

      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <HistoryPanel
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelectEntry={handleHistoryRestore}
      />

      <ProjectPanel
        isOpen={projectOpen}
        onClose={() => setProjectOpen(false)}
      />

      <ShortcutsPanel
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <ToastContainer />
      <PWAUpdatePrompt />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      {/* Org sign-in gate — inside ThemeProvider so the sign-in screen themes,
          outside everything else so no app code runs unauthenticated. */}
      <AuthGate>
        <ToastProvider>
          <SettingsProvider>
            <HistoryProvider>
              <ProjectProvider>
                <AppContent />
              </ProjectProvider>
            </HistoryProvider>
          </SettingsProvider>
        </ToastProvider>
      </AuthGate>
    </ThemeProvider>
  );
}

export default App;
